import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  Gift, 
  DollarSign, 
  Clock, 
  Users, 
  TrendingUp,
  Package,
  Search,
  Filter,
  Download,
  Calendar,
  Star,
  Settings,
  BarChart3
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const AdminProductsAllocations = () => {
  const [products, setProducts] = useState([]);
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('products');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showGrantModal, setShowGrantModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [filters, setFilters] = useState({
    phase: 'all',
    level: 'all',
    category: 'all',
    status: 'all'
  });
  
  // Configuration data
  const [categories, setCategories] = useState([]);
  const [phases, setPhases] = useState([]);
  const [levels, setLevels] = useState([]);

  useEffect(() => {
    loadConfiguration();
    loadProducts();
    loadGrants();
  }, []);

  const loadConfiguration = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const [categoriesRes, phasesRes, levelsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/admin/products/config/categories`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_BASE_URL}/api/admin/products/config/phases`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_BASE_URL}/api/admin/products/config/levels`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setCategories(categoriesRes.data.categories || []);
      setPhases(phasesRes.data.phases || []);
      setLevels(levelsRes.data.levels || []);
    } catch (error) {
      console.error('Error loading configuration:', error);
      toast.error('Failed to load configuration');
    }
  };

  const loadProducts = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const params = new URLSearchParams();
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== 'all') params.append(key, value);
      });
      
      const response = await axios.get(`${API_BASE_URL}/api/admin/products?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.ok) {
        setProducts(response.data.products);
      }
    } catch (error) {
      console.error('Error loading products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const loadGrants = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API_BASE_URL}/api/admin/grants`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.ok) {
        setGrants(response.data.grants);
      }
    } catch (error) {
      console.error('Error loading grants:', error);
      toast.error('Failed to load grants');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'paused': return 'bg-yellow-100 text-yellow-800';
      case 'archived': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPhaseColor = (phase) => {
    switch (phase) {
      case 'phase_1': return 'bg-blue-100 text-blue-800';
      case 'phase_2': return 'bg-purple-100 text-purple-800';
      case 'both': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'invitee': return 'bg-gray-100 text-gray-800';
      case 'rookie': return 'bg-blue-100 text-blue-800';
      case 'rookie_pro': return 'bg-green-100 text-green-800';
      case 'professional_builder': return 'bg-purple-100 text-purple-800';
      case 'enterprise': return 'bg-yellow-100 text-yellow-800';
      case 'all_levels': return 'bg-indigo-100 text-indigo-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const ProductCard = ({ product }) => (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {product.featured && <Star className="w-4 h-4 text-yellow-500" />}
            <CardTitle className="text-lg">{product.title}</CardTitle>
          </div>
          <div className="flex items-center space-x-2">
            <Badge className={getStatusColor(product.status)}>
              {product.status.toUpperCase()}
            </Badge>
            <Button size="sm" variant="outline" onClick={() => setSelectedProduct(product)}>
              <Edit className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <CardDescription className="line-clamp-2">{product.description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center space-x-2">
            <Badge className={getPhaseColor(product.phase)}>
              {product.phase.replace('_', ' ').toUpperCase()}
            </Badge>
            <Badge className={getLevelColor(product.level)}>
              {product.level.replace('_', ' ').toUpperCase()}
            </Badge>
          </div>
          <div className="flex items-center space-x-2 text-gray-600">
            <Package className="w-4 h-4" />
            <span>{product.category.replace('_', ' ')}</span>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <div className="flex items-center space-x-1 text-gray-600">
              <DollarSign className="w-4 h-4" />
              <span>Cash: ${product.cash_cost}</span>
            </div>
            <div className="flex items-center space-x-1 text-gray-600">
              <Gift className="w-4 h-4" />
              <span>Credits: {product.credit_cost}</span>
            </div>
          </div>
          <div>
            <div className="flex items-center space-x-1 text-gray-600">
              <Users className="w-4 h-4" />
              <span>Granted: {product.total_granted || 0}</span>
            </div>
            <div className="flex items-center space-x-1 text-gray-600">
              <TrendingUp className="w-4 h-4" />
              <span>Purchased: {product.total_purchased || 0}</span>
            </div>
          </div>
        </div>
        
        <div className="flex items-center space-x-2 pt-2">
          <Button size="sm" variant="outline" onClick={() => handleGrantProduct(product)}>
            <Gift className="w-4 h-4 mr-1" />
            Grant
          </Button>
          <Button size="sm" variant="outline">
            <BarChart3 className="w-4 h-4 mr-1" />
            Analytics
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const handleGrantProduct = (product) => {
    setSelectedProduct(product);
    setShowGrantModal(true);
  };

  const GrantModal = () => {
    const [grantData, setGrantData] = useState({
      user_id: '',
      notes: ''
    });
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (e) => {
      e.preventDefault();
      setSubmitting(true);
      
      try {
        const token = localStorage.getItem('auth_token');
        const response = await axios.post(
          `${API_BASE_URL}/api/admin/products/${selectedProduct.id}/grant`,
          grantData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data.ok) {
          toast.success('Product granted successfully');
          setShowGrantModal(false);
          loadGrants();
        }
      } catch (error) {
        console.error('Error granting product:', error);
        toast.error(error.response?.data?.detail || 'Failed to grant product');
      } finally {
        setSubmitting(false);
      }
    };

    if (!showGrantModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
          <h3 className="text-lg font-semibold mb-4">Grant Product: {selectedProduct?.title}</h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                User ID *
              </label>
              <Input
                value={grantData.user_id}
                onChange={(e) => setGrantData(prev => ({ ...prev, user_id: e.target.value }))}
                placeholder="Enter user ID"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <Textarea
                value={grantData.notes}
                onChange={(e) => setGrantData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Optional notes about this grant"
                rows={3}
              />
            </div>
            
            <div className="flex items-center space-x-2 pt-4">
              <Button type="submit" disabled={submitting} className="bg-green-600 hover:bg-green-700">
                {submitting ? 'Granting...' : 'Grant Product'}
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowGrantModal(false)}
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/3"></div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-64 bg-gray-200 rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Products & Allocations</h1>
          <p className="text-gray-600">Manage offerings by phase, level, and category</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-2" />
          Create Product
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="products">Products ({products.length})</TabsTrigger>
          <TabsTrigger value="grants">Grants ({grants.length})</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>

        {/* Products Tab */}
        <TabsContent value="products" className="space-y-6">
          {/* Filters */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Filter className="w-5 h-5" />
                <span>Filters</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phase</label>
                  <Select value={filters.phase} onValueChange={(value) => setFilters(prev => ({ ...prev, phase: value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Phases" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Phases</SelectItem>
                      {phases.map((phase) => (
                        <SelectItem key={phase.value} value={phase.value}>
                          {phase.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Level</label>
                  <Select value={filters.level} onValueChange={(value) => setFilters(prev => ({ ...prev, level: value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Levels" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Levels</SelectItem>
                      {levels.map((level) => (
                        <SelectItem key={level.value} value={level.value}>
                          {level.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <Select value={filters.category} onValueChange={(value) => setFilters(prev => ({ ...prev, category: value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Categories" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {categories.map((category) => (
                        <SelectItem key={category.value} value={category.value}>
                          {category.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <Select value={filters.status} onValueChange={(value) => setFilters(prev => ({ ...prev, status: value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="paused">Paused</SelectItem>
                      <SelectItem value="archived">Archived</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="flex items-center space-x-2 mt-4">
                <Button onClick={loadProducts}>
                  <Search className="w-4 h-4 mr-2" />
                  Apply Filters
                </Button>
                <Button variant="outline" onClick={() => {
                  setFilters({ phase: 'all', level: 'all', category: 'all', status: 'all' });
                  loadProducts();
                }}>
                  Clear Filters
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Products Grid */}
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>

          {products.length === 0 && (
            <Card>
              <CardContent className="text-center py-12">
                <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Products Found</h3>
                <p className="text-gray-600 mb-4">No products match your current filters.</p>
                <Button onClick={() => setShowCreateModal(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create First Product
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Grants Tab */}
        <TabsContent value="grants" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Product Grants</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2">User</th>
                      <th className="text-left p-2">Product</th>
                      <th className="text-left p-2">Method</th>
                      <th className="text-left p-2">Date</th>
                      <th className="text-left p-2">Status</th>
                      <th className="text-left p-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grants.map((grant) => (
                      <tr key={grant.grant.id} className="border-b hover:bg-gray-50">
                        <td className="p-2">{grant.user.name}</td>
                        <td className="p-2">{grant.product.title}</td>
                        <td className="p-2">
                          <Badge variant="outline">
                            {grant.grant.granted_by.replace('_', ' ').toUpperCase()}
                          </Badge>
                        </td>
                        <td className="p-2">
                          {new Date(grant.grant.created_at).toLocaleDateString()}
                        </td>
                        <td className="p-2">
                          <Badge className={grant.grant.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                            {grant.grant.is_active ? 'Active' : 'Revoked'}
                          </Badge>
                        </td>
                        <td className="p-2">
                          {grant.grant.is_active && (
                            <Button size="sm" variant="outline">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center space-x-2">
                  <Package className="w-5 h-5 text-blue-500" />
                  <div>
                    <div className="text-2xl font-bold">{products.length}</div>
                    <div className="text-sm text-gray-600">Total Products</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center space-x-2">
                  <Gift className="w-5 h-5 text-green-500" />
                  <div>
                    <div className="text-2xl font-bold">{grants.length}</div>
                    <div className="text-sm text-gray-600">Total Grants</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center space-x-2">
                  <Users className="w-5 h-5 text-purple-500" />
                  <div>
                    <div className="text-2xl font-bold">
                      {products.filter(p => p.status === 'active').length}
                    </div>
                    <div className="text-sm text-gray-600">Active Products</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center space-x-2">
                  <DollarSign className="w-5 h-5 text-yellow-500" />
                  <div>
                    <div className="text-2xl font-bold">
                      ${products.reduce((sum, p) => sum + (p.revenue_generated || 0), 0).toFixed(2)}
                    </div>
                    <div className="text-sm text-gray-600">Total Revenue</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Grant Modal */}
      <GrantModal />
    </div>
  );
};

export default AdminProductsAllocations;